import * as THREE from 'three';
import { SENDA } from '@somnhold/content';
import { camsLayout, deepLayout, emberLayout, hexToWorld, type BattleState, type CamsLayout, type DeepLayout, type EmberLayout, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The heart (docs/sendas/artifice.md, chapter 3, «El corazón»), for the artificer's path: the Hondura under the Flank
 * Cliff, where the titan's heat runs. Red rock and dwarven stonework, warm light from below and a cold one from above,
 * the magma the brightest thing in every place.
 *
 * - `deepRoad` (act 1): the clan's old causeway winding down, paved in flags with a worn middle, walls of dressed stone
 *   either side with a pilaster and a cold lantern niche every few rows; the vents across it, an iron grate over a pit
 *   with a carved rim and a nozzle in each wall: quiet they smoulder, warned (3 s) the pit and the grate glow red and
 *   sparks rise, blowing a sheet of fire stands over the band. The side holes where the ash comes out; the clan's chalk
 *   on the wall (a hammer and a key, fresh); at the foot the Médula's mouth, a great carved gate on a red glow.
 * - `emberRiver` (act 2): a cavern; the river of magma along its north (crust plates on it, bright cracks, the far
 *   wall red-lit, a fall of magma into it), the bank of red rock with faint seams of fire; the old trip-hammer on the
 *   bank (its frame, helve and head, the wheel dipping in the river), jammed: chains round the wheel and wedges in the
 *   helve that come off as Dunna works, the clan's tools about it; the heart stone in the rock beside it, beating; two
 *   tunnels in from the south.
 * - `tripHammer` (act 3, the test): the same bank, the hammer free: the shaft along the bank on trestles, its six cam
 *   places each with its number on an iron plate; the cams (red iron lobes) where they sit, lie on their rack or are
 *   carried, turning with the shaft; at its west end the stone on its base, six faces with their numbers cut in, the
 *   hammer over it; the veins' order over the stone (done white, next red and beating, to come dim); the wheel at the
 *   east end; the lever. The magma's spots coming up (a red disc filling, then fire), La Tenaza's pincers' arc, its
 *   grip on the wheel (sparks), its splash (the bank glowing red at its rim).
 * - `emberSlab` (the campfire): a warm slab of rock by the river at night. `rajaWall` (the opening's cinematic): Grada's
 *   wall at dawn, the Raja opening over it and shedding stones.
 *
 * Draw calls: the dressing (one merged mesh) and its far rock (one more), the seams of fire (one), the lava (one or two
 * planes), the flames (one instanced mesh), a glow plane per vent, the hammer's parts, the plates and tablets, the
 * cams, the magma's discs (a pool of eight), the glow layer, the sky. No lights of its own: the board's are repainted.
 * Nothing here walks the nightmares; the sim's events and its state are read once a tick.
 */

export const CORAZON_PLACES = new Set(['deepRoad', 'emberRiver', 'tripHammer', 'emberSlab', 'rajaWall']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const FIRE = new THREE.Color('#ff7a2a');
const WARM = new THREE.Color('#ffb48a');
const WHITE = new THREE.Color('#ffffff');
const ROCK = ['#73534a', '#664840', '#7e5d52', '#5a4038'];
const DRESSED = ['#77625a', '#6b5851', '#816b62', '#5f4e48'];
const FLAG = ['#5f4f49', '#574842', '#6a5952'];
const DARK = ['#2a201d', '#231b19', '#332824'];
const IRON = '#2c2f35';
const WOOD = ['#6a5242', '#574336', '#7a5e48'];
/** The test's trip-hammer: the base's (and shaft's) height, its radius and length; the helve's pivot behind it. */
const DRUM_Y = 1.45;
const DRUM_R = 1.15;
const DRUM_LEN = 1.5;
const HELVE_Y = 3.75;
const HELVE_BACK = 2.3;

/** The light inside the Hondura: warm from the magma below, a cold one from above; outdoors at dawn for the Raja. */
const LOOK = {
  inside: { bg: '#1e110d', fog: ['#28150f', 26, 80] as [string, number, number], hemi: ['#a8b0c2', '#8a3418', 2.1] as [string, string, number], key: ['#ffd8c0', 1.9] as [string, number], rim: ['#ff6236', 1.6] as [string, number] },
  dawn: { bg: '#5c6880', fog: ['#646f86', 30, 120] as [string, number, number], hemi: ['#ccd4e2', '#22252c', 1.6] as [string, string, number], key: ['#ffe2d2', 2.4] as [string, number], rim: ['#a9bfee', 1.5] as [string, number] },
};

/** The river of magma: crust plates drifting on it, bright cracks between, hotter where it runs. */
const lavaMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, heat: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform float heat; varying vec2 vXZ;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        vec2 p = vXZ * 0.45 + vec2(-time * 0.18, sin(vXZ.x * 0.2 + time * 0.3) * 0.15);
        float c = n(p) * 0.6 + n(p * 2.3 + 7.1) * 0.3 + n(p * 5.1 - time * 0.2) * 0.1;
        // Crust where it is high, cracks of fire where it is low.
        float crack = 1.0 - smoothstep(0.32, 0.5, c);
        vec3 crust = mix(vec3(0.12, 0.045, 0.03), vec3(0.24, 0.08, 0.04), n(p * 3.0));
        vec3 fire = mix(vec3(1.0, 0.32, 0.08), vec3(1.0, 0.78, 0.35), smoothstep(0.0, 0.32, 0.32 - c));
        vec3 col = mix(crust, fire * (1.15 + heat * 0.6), crack);
        // A slow throb of heat over all of it.
        col += vec3(0.25, 0.06, 0.02) * (0.5 + 0.5 * sin(time * 1.3 + vXZ.x * 0.3)) * (0.4 + heat);
        gl_FragColor = vec4(col, 1.0);
      }`,
  });

/** A fall of magma down the far wall: streaks running down, bright at the lip. */
const fallMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 } },
    transparent: true,
    depthWrite: false,
    vertexShader: /* glsl */ `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform float time; varying vec2 vUv;
      float h(float x) { return fract(sin(x * 91.7) * 43758.5453); }
      void main() {
        float col = floor(vUv.x * 14.0);
        float s = fract(vUv.y * 2.0 + time * (0.6 + h(col) * 0.5) + h(col + 3.0));
        float edge = smoothstep(0.0, 0.18, vUv.x) * smoothstep(1.0, 0.82, vUv.x);
        float a = (0.55 + 0.45 * s) * edge;
        vec3 c = mix(vec3(1.0, 0.35, 0.08), vec3(1.0, 0.8, 0.4), s * vUv.y);
        gl_FragColor = vec4(c, a);
      }`,
  });

/** A chalk drawing on transparent: the clan's mark, a hammer and a key, fresh. */
const chalkTex = (): THREE.Texture => {
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return new THREE.Texture();
  c.width = c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    g.strokeStyle = 'rgba(236,240,244,0.95)';
    g.lineWidth = 7;
    g.lineCap = 'round';
    // The hammer: a haft and its head.
    g.beginPath();
    g.moveTo(40, 104);
    g.lineTo(62, 34);
    g.moveTo(42, 30);
    g.lineTo(84, 42);
    g.stroke();
    // The key: a ring and its bit.
    g.beginPath();
    g.arc(86, 90, 12, 0, Math.PI * 2);
    g.moveTo(86, 78);
    g.lineTo(86, 34);
    g.lineTo(98, 34);
    g.moveTo(86, 46);
    g.lineTo(96, 46);
    g.stroke();
  }
  return new THREE.CanvasTexture(c);
};

/** A number cut in a plate: pale stone on dark iron (or bright on clear, `bare`). */
const numTex = (text: string, bare = false): THREE.Texture => {
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return new THREE.Texture();
  c.width = c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    if (!bare) {
      g.fillStyle = '#1b1d21';
      g.fillRect(0, 0, 128, 128);
      g.strokeStyle = '#8a9098';
      g.lineWidth = 8;
      g.strokeRect(6, 6, 116, 116);
    }
    g.fillStyle = '#ffffff';
    g.font = '800 88px Georgia, serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(text, 64, 70);
  }
  const t = new THREE.CanvasTexture(c);
  t.anisotropy = 4;
  return t;
};

/** A soft round glow (the Médula's mouth, the river's haze). */
const glowTex = (inner: string, outer: string): THREE.Texture => {
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return new THREE.Texture();
  c.width = c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    const gr = g.createRadialGradient(64, 64, 4, 64, 64, 64);
    gr.addColorStop(0, inner);
    gr.addColorStop(1, outer);
    g.fillStyle = gr;
    g.fillRect(0, 0, 128, 128);
  }
  return new THREE.CanvasTexture(c);
};

interface CamView {
  g: THREE.Group;
  lobe: THREE.Group;
  pos: THREE.Vector3;
}

export class CorazonLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private texs: THREE.Texture[] = [];
  private glow = new GlowLayer(320);
  private dome: SkyDome | null = null;
  private now = 0;
  private outside: boolean;
  private open = new Set<string>();
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private last: BattleState | null = null;
  private v3 = new THREE.Vector3();
  private lavas: THREE.ShaderMaterial[] = [];
  private embers: THREE.Vector3[] = [];
  private puff = 0;
  // The deep road.
  private D: DeepLayout | null = null;
  private ventGlow: { mat: THREE.MeshBasicMaterial; grate: THREE.MeshStandardMaterial; at: THREE.Vector3; w: number }[] = [];
  private flames: THREE.InstancedMesh | null = null;
  private flameAt: { v: number; p: THREE.Vector3; s: number; k: number }[] = [];
  private mouthGlow: THREE.Vector3 | null = null;
  // The river and the hammer.
  private E: EmberLayout | null = null;
  private C: CamsLayout | null = null;
  private wheel: THREE.Group | null = null;
  private helve: THREE.Group | null = null;
  private chains: THREE.Object3D[] = [];
  private stoneGlow: THREE.Vector3 | null = null;
  private heat = 0;
  // The cams.
  private drum: THREE.Group | null = null;
  private drumAngle = -Math.PI / 6;
  private cams: CamView[] = [];
  private hubAt: THREE.Vector3[] = [];
  private rackAt: THREE.Vector3[] = [];
  private tablets: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial }[] = [];
  private lever: THREE.Group | null = null;
  private leverPull = 0;
  private strikeFlash = 0;
  private chipMarks: THREE.Mesh[] = [];
  private chipMat: THREE.MeshStandardMaterial | null = null;
  private faceAt: THREE.Vector3 | null = null;
  private facePlates: { mat: THREE.MeshBasicMaterial; f: number; vein: THREE.MeshBasicMaterial | null }[] = [];
  private shaftG: THREE.Group | null = null;
  private headFace: THREE.MeshBasicMaterial | null = null;
  private struck = -1;
  // The magma's spots, the pincers' arc, the splash.
  private discs: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial }[] = [];
  private arc: THREE.Mesh | null = null;
  private arcMat: THREE.MeshBasicMaterial | null = null;
  private rimMat: THREE.MeshBasicMaterial | null = null;
  private splashFlash = 0;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `corazon-${place}`;
    this.outside = place === 'rajaWall';
    const kit = new Kit();
    const far = new Kit();
    const hot = new Kit();
    if (place === 'deepRoad') this.buildRoad(kit, far, hot, s);
    else if (place === 'emberRiver') this.buildRiver(kit, far, hot, s);
    else if (place === 'tripHammer') this.buildCams(kit, far, hot, s);
    else if (place === 'emberSlab') this.buildSlab(kit, far, hot);
    else this.buildRaja(kit, far);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88, metalness: 0.05, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'corazon-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    const farMesh = far.build(mat, 'corazon-far');
    if (farMesh) {
      this.geos.push(farMesh.geometry);
      this.group.add(farMesh);
    }
    // The seams of fire, the embers in the rock: unlit, bright.
    const hotMat = new THREE.MeshBasicMaterial({ vertexColors: true, fog: false });
    this.mats.push(hotMat);
    const hotMesh = hot.build(hotMat, 'corazon-hot');
    if (hotMesh) {
      this.geos.push(hotMesh.geometry);
      this.group.add(hotMesh);
    }
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  /** Kept to dispose with the layer (never `std()`'s: those are cached and shared, materials.ts). */
  private track<T extends THREE.Material | THREE.BufferGeometry | THREE.Texture>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else if (x instanceof THREE.Texture) this.texs.push(x);
    else this.geos.push(x);
    return x;
  }

  private std(color: string, o: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
    return this.track(new THREE.MeshStandardMaterial({ color, roughness: 0.8, metalness: 0.1, flatShading: true, ...o }));
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: new THREE.Color(color), size, a, seed: this.lights.length * 1.37, flick });
  }

  private setOpen(cells: { q: number; r: number }[]): void {
    for (const h of cells) this.open.add(`${h.q},${h.r}`);
  }

  /** Whether a world point is off the open cells (cut out of the board's ground). */
  private voidAt = (x: number, z: number): boolean => {
    if (!this.open.size) return false;
    const hr = Math.round(z / (S * 0.866));
    const hq = Math.round(x / S - hr / 2);
    for (const [dq, dr] of [
      [0, 0],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, -1],
      [-1, 1],
    ] as const) {
      const k = `${hq + dq},${hr + dr}`;
      if (!this.open.has(k)) continue;
      const c = hexToWorld(hq + dq, hr + dr);
      if ((x - c.x * K) ** 2 + (z - c.y * K) ** 2 < (0.62 * S) ** 2) return false;
    }
    return true;
  };

  /** The ground off the open cells cut out (the walls, the river stand there instead). */
  private carve(terrain: TerrainBuild): void {
    const mesh = terrain.mesh;
    const geo = mesh.geometry;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!pos) return;
    mesh.updateMatrixWorld(true);
    const mw = mesh.matrixWorld;
    const v = new THREE.Vector3();
    const idx = geo.index;
    const tri = (i: number): number => (idx ? idx.getX(i) : i);
    const n = idx ? idx.count : pos.count;
    const keep: number[] = [];
    for (let i = 0; i < n; i += 3) {
      let cx = 0;
      let cz = 0;
      for (let k = 0; k < 3; k++) {
        v.fromBufferAttribute(pos, tri(i + k)).applyMatrix4(mw);
        cx += v.x;
        cz += v.z;
      }
      const out = this.voidAt(cx / 3, cz / 3);
      if (idx) {
        if (!out) keep.push(tri(i), tri(i + 1), tri(i + 2));
      } else if (out) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);
        pos.setXYZ(i + 1, x, y, z);
        pos.setXYZ(i + 2, x, y, z);
      }
    }
    if (idx) geo.setIndex(keep);
    pos.needsUpdate = true;
    geo.computeBoundingSphere();
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground' || o.name === 'roads') o.visible = false;
    });
  }

  /** A slab of dark rock under everything (what shows past the walls instead of nothing). */
  private underfloor(kit: Kit, y = -0.35, color = DARK[0] as string): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, color, place(0, y - 0.25, 0, 0, new THREE.Vector3(70, 0.5, 70)));
    box.dispose();
  }

  /** A lump of rough rock (several boxes and a dodecahedron), its top at `top`. */
  private rock(kit: Kit, x: number, z: number, top: number, w: number, k: number, colors = ROCK): void {
    const dode = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const j = Math.sin(x * 12.9 + z * 7.3) * 0.5 + 0.5;
    kit.add(dode, colors[Math.floor(j * 3.99)] as string, place(x, top * 0.5 - 0.2, z, j * 3 + k, new THREE.Vector3(w, top + 0.4, w * (0.8 + j * 0.4))));
    if (j > 0.4) kit.add(box, colors[(Math.floor(j * 7) + 1) % 4] as string, place(x + 0.2, top * 0.75, z - 0.15, j * 5, new THREE.Vector3(w * 0.5, top * 0.5, w * 0.45), 0.2, -0.15));
    dode.dispose();
    box.dispose();
  }

  /** The magma's discs: a pool of eight, shown where a spot is coming up. */
  private discPool(): void {
    const geo = this.track(new THREE.CircleGeometry(1, 28).rotateX(-Math.PI / 2));
    for (let i = 0; i < 8; i++) {
      const mat = this.track(new THREE.MeshBasicMaterial({ color: RED, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
      const mesh = new THREE.Mesh(geo, mat);
      mesh.visible = false;
      mesh.renderOrder = 3;
      this.group.add(mesh);
      this.discs.push({ mesh, mat });
    }
  }

  /* ---------- the deep road ---------- */

  private buildRoad(kit: Kit, far: Kit, hot: Kit, s: BattleState): void {
    const L = deepLayout(s.seed, [SENDA.vents.beat[0], SENDA.vents.beat[1]]);
    this.D = L;
    this.setOpen([...L.road, ...L.holes]);
    this.underfloor(far, -0.6, DARK[1]);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    const key = (q: number, r: number): string => `${q},${r}`;
    const road = new Set(L.road.map((h) => key(h.q, h.r)));
    const holes = new Set(L.holes.map((h) => key(h.q, h.r)));
    // The walls: a cell off the road on either side of every row is dressed stone, a course of blocks with a cap; every
    // third row a pilaster with a lantern niche; past the walls, rough rock rising.
    const rows = [...new Set(L.road.map((h) => h.r))].sort((a, b) => a - b);
    rows.forEach((r, ri) => {
      const cells = L.road.filter((h) => h.r === r).sort((a, b) => a.q - b.q);
      const west = cells[0];
      const east = cells[cells.length - 1];
      if (!west || !east) return;
      for (const [side, hq] of [
        [-1, west.q - 1],
        [1, east.q + 1],
      ] as const) {
        if (holes.has(key(hq, r)) || road.has(key(hq, r))) {
          // A side hole: an arch of dressed stone, a dark recess, a red glow deep in it.
          const p = this.hex(hq, r, 0);
          const dx = side * S * 0.42;
          for (const k of [-1, 1]) kit.add(box, DRESSED[2] as string, place(p.x + dx, 0.8, p.z + k * 0.5, 0, new THREE.Vector3(0.36, 1.6, 0.3)));
          kit.add(box, DRESSED[0] as string, place(p.x + dx, 1.7, p.z, 0, new THREE.Vector3(0.42, 0.26, 1.34)));
          kit.add(box, DARK[2] as string, place(p.x + side * S * 0.62, 0.7, p.z, 0, new THREE.Vector3(0.3, 1.4, 1.0)));
          this.light(this.v3.set(p.x + side * S * 0.55, 0.5, p.z), '#c2361e', 1.6, 0.55, 0.4);
          continue;
        }
        const p = this.hex(hq, r, 0);
        const j = Math.sin(ri * 3.7 + side) * 0.5 + 0.5;
        const h = 1.25 + j * 0.25;
        // Two courses of blocks, staggered, a cap stone.
        kit.add(box, DRESSED[(ri + (side > 0 ? 1 : 0)) % 4] as string, place(p.x, h * 0.28, p.z - 0.25, 0, new THREE.Vector3(S * 0.98, h * 0.56, 0.62)));
        kit.add(box, DRESSED[(ri + 2) % 4] as string, place(p.x, h * 0.28, p.z + 0.3, 0, new THREE.Vector3(S * 0.98, h * 0.56, 0.6)));
        kit.add(box, DRESSED[(ri + 3) % 4] as string, place(p.x + side * 0.05, h * 0.78, p.z, 0, new THREE.Vector3(S * 0.96, h * 0.44, 1.04)));
        kit.add(box, '#4f403b', place(p.x + side * 0.08, h + 0.05, p.z, 0, new THREE.Vector3(S * 1.0, 0.1, 1.1)));
        // The joints between blocks: dark lines.
        kit.add(box, '#3a2e2a', place(p.x - side * S * 0.5, h * 0.5, p.z, 0, new THREE.Vector3(0.03, h * 0.98, 1.08)));
        if (ri % 3 === 1) {
          // A pilaster with its lantern: cold iron, a little warm light.
          kit.add(box, DRESSED[1] as string, place(p.x - side * S * 0.34, 1.0, p.z, 0, new THREE.Vector3(0.38, 2.0, 0.5)));
          kit.add(box, DRESSED[2] as string, place(p.x - side * S * 0.34, 2.05, p.z, 0, new THREE.Vector3(0.5, 0.14, 0.62)));
          kit.add(box, IRON, place(p.x - side * S * 0.52, 1.45, p.z, 0, new THREE.Vector3(0.14, 0.22, 0.14)));
          kit.add(box, '#e6a070', place(p.x - side * S * 0.52, 1.45, p.z, 0, new THREE.Vector3(0.09, 0.14, 0.09)));
          this.light(this.v3.set(p.x - side * S * 0.56, 1.45, p.z), WARM, 1.5, 0.6, 0.8);
        }
        // Rough rock behind the wall, rising away from the road.
        for (let k = 1; k <= 3; k++) this.rock(far, p.x + side * (S * 0.45 + k * 0.85), p.z + (k % 2) * 0.35, 1.2 + k * 0.45, 0.85 + k * 0.12, k + ri, k === 3 ? [...DARK, '#2e2320'] : k === 2 ? ['#4a342e', '#3f2c27', '#55392f', '#45302a'] : ROCK);
      }
    });
    // The road's flags: worn in the middle, a gutter of dark iron along one side.
    for (const h of L.road) {
      const p = this.hex(h.q, h.r, 0);
      for (let k = 0; k < 4; k++) {
        const j = Math.sin(h.q * 7.1 + h.r * 3.3 + k) * 0.5 + 0.5;
        kit.add(box, FLAG[(k + h.q + h.r + 9) % 3] as string, place(p.x + ((k % 2) - 0.5) * 0.54, 0.02 + j * 0.02, p.z + (Math.floor(k / 2) - 0.5) * 0.46, j * 0.1, new THREE.Vector3(0.5, 0.06, 0.42)));
      }
    }
    // The vents: an iron grate over a glowing pit, a carved rim, a nozzle in each wall.
    const flamePts: { v: number; p: THREE.Vector3; s: number; k: number }[] = [];
    L.vents.forEach((v, vi) => {
      const cells = L.ventCells[vi] ?? [];
      const xs = cells.map((h) => this.hex(h.q, h.r, 0));
      const cx = xs.reduce((a, p) => a + p.x, 0) / Math.max(1, xs.length);
      const z0 = v.y0 * K;
      const z1 = v.y1 * K;
      const zc = (z0 + z1) / 2;
      const w = 3.3 * S;
      // The rim: dressed stone round the pit.
      for (const z of [z0 + 0.08, z1 - 0.08]) kit.add(box, DRESSED[0] as string, place(cx, 0.06, z, 0, new THREE.Vector3(w, 0.14, 0.16)));
      // The pit, deep and dark (the glow plane lies over it).
      kit.add(box, '#140b08', place(cx, -0.02, zc, 0, new THREE.Vector3(w - 0.3, 0.04, z1 - z0 - 0.34)));
      // The grate's bars: across the road, and two along.
      const grate = this.track(new THREE.MeshStandardMaterial({ color: '#33363c', roughness: 0.5, metalness: 0.6, emissive: new THREE.Color('#000000') }));
      const bars = new THREE.Group();
      const barGeo = this.track(new THREE.BoxGeometry(1, 1, 1));
      for (let k = 0; k < 9; k++) {
        const m = new THREE.Mesh(barGeo, grate);
        m.scale.set(w - 0.35, 0.06, 0.06);
        m.position.set(cx, 0.08, z0 + 0.3 + (k * (z1 - z0 - 0.6)) / 8);
        bars.add(m);
      }
      for (const k of [-1, 0, 1]) {
        const m = new THREE.Mesh(barGeo, grate);
        m.scale.set(0.07, 0.08, z1 - z0 - 0.4);
        m.position.set(cx + k * (w * 0.32), 0.08, zc);
        bars.add(m);
      }
      bars.name = 'corazon-grate';
      this.group.add(bars);
      const gm = this.track(new THREE.MeshBasicMaterial({ color: FIRE, transparent: true, opacity: 0.1, depthWrite: false, blending: THREE.AdditiveBlending }));
      const plane = new THREE.Mesh(this.track(new THREE.PlaneGeometry(w - 0.3, z1 - z0 - 0.3).rotateX(-Math.PI / 2)), gm);
      plane.position.set(cx, 0.03, zc);
      plane.renderOrder = 2;
      this.group.add(plane);
      this.ventGlow.push({ mat: gm, grate, at: new THREE.Vector3(cx, 0.1, zc), w });
      // The nozzles in the walls: iron mouths, each side.
      for (const k of [-1, 1]) {
        kit.add(cyl, IRON, place(cx + k * (w / 2 + 0.05), 0.55, zc, 0, new THREE.Vector3(0.36, 0.4, 0.36), 0, (k * Math.PI) / 2));
        kit.add(box, '#1a0c08', place(cx + k * (w / 2 - 0.12), 0.55, zc, 0, new THREE.Vector3(0.06, 0.24, 0.24)));
      }
      // The flames' spots across the band.
      for (let i = 0; i < 7; i++) for (const zz of [z0 + 0.45, z1 - 0.45]) flamePts.push({ v: vi, p: new THREE.Vector3(cx - w / 2 + 0.3 + (i * (w - 0.6)) / 6, 0.05, zz + Math.sin(i * 3.1) * 0.15), s: 0.75 + Math.sin(i * 5.3 + vi) * 0.25, k: i * 1.7 + vi });
      this.light(new THREE.Vector3(cx, 0.15, zc), FIRE, 2.2, 0.22, 0.7);
    });
    this.flameAt = flamePts;
    const fGeo = this.track(new THREE.ConeGeometry(0.3, 1, 7, 1, true).translate(0, 0.5, 0));
    const fMat = this.track(new THREE.MeshBasicMaterial({ color: '#ff6a24', transparent: true, opacity: 0.7, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }));
    this.flames = new THREE.InstancedMesh(fGeo, fMat, Math.max(1, flamePts.length));
    this.flames.count = 0;
    this.flames.frustumCulled = false;
    this.flames.renderOrder = 4;
    this.group.add(this.flames);
    // The clan's chalk: on the wall, fresh.
    const tex = this.track(chalkTex());
    const chalk = this.track(new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }));
    for (const c of L.chalkAt) {
      const m = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.75, 0.75)), chalk);
      const p = this.ctx.at(c.x, c.y, 0.95);
      const right = c.x > (L.way[0]?.x ?? 0) + 500 ? 1 : -1;
      m.position.set(p.x - right * 0.05, 0.95, p.z);
      m.rotation.y = right > 0 ? -Math.PI / 2 : Math.PI / 2;
      this.group.add(m);
    }
    // The way in at the top: a stair coming down out of the dark.
    const top = L.way[0] as { x: number; y: number };
    const tp = this.ctx.at(top.x, top.y, 0);
    for (let k = 0; k < 4; k++) kit.add(box, FLAG[k % 3] as string, place(tp.x, 0.12 + k * 0.16, tp.z - 1.0 - k * 0.42, 0, new THREE.Vector3(3.2, 0.16, 0.5)));
    // The Médula's mouth at the foot: two great pillars, a lintel with the clan's runes, the red glow beyond.
    const m = this.ctx.at(L.mouth.x, L.mouth.y, 0);
    for (const k of [-1, 1]) {
      kit.add(box, DRESSED[2] as string, place(m.x + k * 2.2, 1.6, m.z + 0.6, 0, new THREE.Vector3(0.9, 3.2, 0.9)));
      kit.add(box, DRESSED[0] as string, place(m.x + k * 2.2, 0.15, m.z + 0.6, 0, new THREE.Vector3(1.1, 0.3, 1.1)));
      kit.add(box, DRESSED[1] as string, place(m.x + k * 2.2, 3.15, m.z + 0.6, 0, new THREE.Vector3(1.1, 0.2, 1.1)));
    }
    kit.add(box, DRESSED[3] as string, place(m.x, 3.45, m.z + 0.6, 0, new THREE.Vector3(5.6, 0.55, 1.0)));
    for (let k = -3; k <= 3; k++) hot.add(box, '#ff6a2a', place(m.x + k * 0.6, 3.45, m.z + 1.11, 0, new THREE.Vector3(0.16, 0.24, 0.02)));
    const mg = this.track(new THREE.MeshBasicMaterial({ map: this.track(glowTex('rgba(255,180,90,1)', 'rgba(255,60,20,0)')), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false }));
    const mouth = new THREE.Mesh(this.track(new THREE.PlaneGeometry(4.2, 3.4)), mg);
    mouth.position.set(m.x, 1.5, m.z + 1.4);
    this.group.add(mouth);
    kit.add(box, '#1c0d08', place(m.x, 1.5, m.z + 1.6, 0, new THREE.Vector3(4.0, 3.0, 0.2)));
    this.mouthGlow = new THREE.Vector3(m.x, 1.2, m.z + 1.0);
    this.light(this.mouthGlow, '#ff7a3a', 6.5, 0.6, 0.3);
    // Seams of fire in the walls' rock now and then, warmer toward the Médula.
    for (let i = 0; i < 26; i++) {
      const w = L.way[Math.floor((i * (L.way.length - 1)) / 25)] as { x: number; y: number };
      const side = i % 2 === 0 ? -1 : 1;
      const p = this.ctx.at(w.x + side * (2300 + (i % 3) * 400), w.y + ((i * 377) % 600) - 300, 0);
      hot.add(box, i > 15 ? '#ff7a2a' : '#c2361e', place(p.x, 0.4 + (i % 4) * 0.35, p.z, i * 0.7, new THREE.Vector3(0.5 + (i % 3) * 0.2, 0.04, 0.05), 0, (i % 5) * 0.3 - 0.6));
      this.embers.push(new THREE.Vector3(p.x, 0.4 + (i % 4) * 0.35, p.z));
    }
    box.dispose();
    cyl.dispose();
  }

  /* ---------- the river and the hammer ---------- */

  /** The river of magma along the north, the far wall over it red-lit, a fall of magma into it. */
  private river(kit: Kit, far: Kit, hot: Kit, edgeR: number): void {
    const zEdge = edgeR * 0.866 * S;
    const lava = this.track(lavaMat());
    this.lavas.push(lava);
    const plane = new THREE.Mesh(this.track(new THREE.PlaneGeometry(60, 14).rotateX(-Math.PI / 2)), lava);
    plane.position.set(0, -0.28, zEdge - 7.0 + 0.45);
    plane.name = 'corazon-lava';
    this.group.add(plane);
    // The bank's lip: broken rock along the edge, glowing underneath.
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (let x = -14; x <= 14; x += 0.8) {
      const j = Math.sin(x * 3.7) * 0.5 + 0.5;
      kit.add(box, ROCK[Math.floor(j * 3.99)] as string, place(x, -0.08, zEdge + 0.3 + j * 0.15, j * 2, new THREE.Vector3(0.9, 0.3 + j * 0.15, 0.5)));
      if (j > 0.6) hot.add(box, '#ff6a2a', place(x + 0.2, -0.22, zEdge + 0.08, 0, new THREE.Vector3(0.6, 0.05, 0.08)));
    }
    // The far wall: rock rising red-lit from below, stalactites hanging toward the river.
    const fz = zEdge - 9.5;
    for (let x = -26; x <= 26; x += 1.6) {
      const j = Math.sin(x * 1.31) * 0.5 + 0.5;
      const j2 = Math.sin(x * 0.53 + 2) * 0.5 + 0.5;
      this.rock(far, x, fz - j * 1.5, 4 + j * 5 + j2 * 3, 2.4, x, ['#5a3027', '#4a2620', '#6a3a2c', '#3e211b']);
      if (j2 > 0.55) hot.add(box, '#ff5a28', place(x, 0.6 + j * 2.4, fz + 1.2, 0, new THREE.Vector3(0.9, 0.06, 0.06), 0, j * 0.8 - 0.4));
    }
    // A fall of magma down the far wall, to the west.
    const fm = this.track(fallMat());
    this.lavas.push(fm);
    const fall = new THREE.Mesh(this.track(new THREE.PlaneGeometry(2.2, 8)), fm);
    fall.position.set(-7.5, 3.4, fz + 1.6);
    fall.renderOrder = 3;
    this.group.add(fall);
    this.light(new THREE.Vector3(-7.5, 0.4, fz + 2.2), '#ffa040', 4.5, 0.55, 0.5);
    for (let x = -12; x <= 12; x += 3) this.light(new THREE.Vector3(x, -0.1, zEdge - 2.5 - Math.abs(Math.sin(x)) * 2), '#ff7a2a', 3.5, 0.32, 0.6);
    for (let k = 0; k < 14; k++) this.embers.push(new THREE.Vector3(-12 + k * 1.8, -0.2, zEdge - 1.5 - (k % 4) * 1.2));
    box.dispose();
  }

  /**
   * The cavern's wall round the bank (east, south, west): a low rim of broken blocks at the floor's edge, then steps of
   * rock rising darker as they go, seams of fire in them now and then (no lumps all alike: three rings, each finer).
   */
  private cavernWall(far: Kit, hot: Kit, rx: number, rz: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (let ring = 0; ring < 3; ring++) {
      const n = 46 - ring * 8;
      for (let a = 0; a < n; a++) {
        const ang = Math.PI * 0.04 + (a / (n - 1)) * Math.PI * 0.92;
        const j = Math.sin(a * 12.9 + ring * 4.1) * 0.5 + 0.5;
        const d = 1 + ring * 0.14 + j * 0.04;
        const x = Math.cos(ang) * rx * d;
        const z = Math.sin(ang) * rz * d - 0.4;
        const hgt = 0.5 + ring * 1.3 + j * (0.6 + ring * 0.6);
        const col = ring === 0 ? ROCK[Math.floor(j * 3.99)] : ring === 1 ? ['#4a342e', '#3f2c27', '#55392f'][Math.floor(j * 2.99)] : DARK[Math.floor(j * 2.99)];
        far.add(box, col as string, place(x, hgt / 2 - 0.1, z, ang + j, new THREE.Vector3(0.8 + j * 0.6 + ring * 0.3, hgt, 0.7 + j * 0.5), (j - 0.5) * 0.2, (j - 0.5) * 0.25));
        if (ring === 1 && j > 0.8) hot.add(box, '#d8401e', place(x * 0.99, hgt * 0.4, z * 0.99, ang, new THREE.Vector3(0.7, 0.04, 0.05), 0, j - 0.5));
      }
    }
    box.dispose();
  }

  /** The bank's floor: a few seams of fire in the red rock. */
  private seams(hot: Kit, cells: { q: number; r: number }[], n: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i < n; i++) {
      const h = cells[(i * 37) % cells.length];
      if (!h) continue;
      const p = this.hex(h.q, h.r, 0.012);
      hot.add(box, i % 3 === 0 ? '#ff7a2a' : '#b8321c', place(p.x + Math.sin(i) * 0.3, 0.012, p.z + Math.cos(i * 1.7) * 0.3, i * 1.3, new THREE.Vector3(0.7 + (i % 3) * 0.3, 0.01, 0.035)));
    }
    box.dispose();
  }

  /** A tunnel's mouth at the bank's edge: an arch of dressed stone on the dark. */
  private tunnel(kit: Kit, at: { x: number; y: number }, edge: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    // At the floor's edge in the spot's direction, facing the cavern's middle.
    const d = Math.max(1, Math.hypot(at.x, at.y));
    const p = this.ctx.at((at.x / d) * edge * 1000, (at.y / d) * edge * 1000, 0);
    const out = new THREE.Vector3(at.x / d, 0, at.y / d);
    const yaw = Math.atan2(out.x, out.z);
    const right = new THREE.Vector3(out.z, 0, -out.x);
    const c = p.clone().addScaledVector(out, 0.9);
    for (const k of [-1, 1]) kit.add(box, DRESSED[1] as string, place(c.x + right.x * k * 1.0, 0.9, c.z + right.z * k * 1.0, yaw, new THREE.Vector3(0.4, 1.8, 0.5)));
    kit.add(box, DRESSED[0] as string, place(c.x, 1.85, c.z, yaw, new THREE.Vector3(2.5, 0.3, 0.6)));
    kit.add(box, '#120a08', place(c.x + out.x * 0.35, 0.85, c.z + out.z * 0.35, yaw, new THREE.Vector3(1.6, 1.7, 0.2)));
    this.light(c.clone().setY(0.6), '#c2361e', 1.8, 0.5, 0.4);
    box.dispose();
  }

  /**
   * The trip-hammer: its frame, its helve and head over the anvil (or the stone's base), the wheel at the shaft's east
   * end dipping in the river. `jammed`: chains round the wheel and wedges in the helve (act 2).
   */
  private hammer(kit: Kit, at: THREE.Vector3, wheelAt: THREE.Vector3, jammed: boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    // The frame: two posts with braces, a beam on top, iron bands.
    for (const k of [-1, 1]) {
      kit.add(box, WOOD[k > 0 ? 0 : 2] as string, place(at.x + k * 0.85, 1.4, at.z - 0.2, 0, new THREE.Vector3(0.28, 2.8, 0.32)));
      kit.add(box, WOOD[1] as string, place(at.x + k * 1.2, 0.7, at.z - 0.2, 0, new THREE.Vector3(0.14, 1.5, 0.14), 0, k * 0.5));
      for (const y of [0.5, 1.9]) kit.add(box, IRON, place(at.x + k * 0.85, y, at.z - 0.2, 0, new THREE.Vector3(0.32, 0.08, 0.36)));
    }
    kit.add(box, WOOD[0] as string, place(at.x, 2.75, at.z - 0.2, 0, new THREE.Vector3(2.0, 0.26, 0.4)));
    // The helve and head, on a pivot at the beam's back: a group, it rises and falls.
    const helve = new THREE.Group();
    helve.position.set(at.x, 2.2, at.z - 1.5);
    const hw = this.std('#6a5242', { roughness: 0.9 });
    const hi = this.std('#4a4f58', { roughness: 0.4, metalness: 0.55 });
    const shaft = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.2, 0.2, 1.7)), hw);
    shaft.position.set(0, 0, 0.75);
    helve.add(shaft);
    const head = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.9, 0.75, 0.85)), hi);
    head.position.set(0, -0.15, 1.5);
    helve.add(head);
    const band = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.74, 0.08, 0.74)), this.std('#5a4e3e', { roughness: 0.5, metalness: 0.5 }));
    band.position.set(0, 0.1, 1.5);
    helve.add(band);
    head.castShadow = true;
    this.group.add(helve);
    this.helve = helve;
    // The shaft from the hammer to the wheel, on trestles.
    const dx = wheelAt.x - at.x;
    const shaftLen = Math.abs(dx);
    kit.add(cyl, '#3a3d44', place(at.x + dx / 2, 0.62, wheelAt.z, 0, new THREE.Vector3(0.18, shaftLen, 0.18), 0, Math.PI / 2));
    for (let k = 1; k < 4; k++) {
      const x = at.x + (dx * k) / 4;
      kit.add(box, WOOD[1] as string, place(x, 0.32, wheelAt.z, 0, new THREE.Vector3(0.16, 0.64, 0.5)));
      kit.add(box, WOOD[0] as string, place(x, 0.62, wheelAt.z, 0, new THREE.Vector3(0.3, 0.1, 0.6)));
    }
    // The wheel: a paddle wheel of iron and timber, half in the river.
    const wheel = new THREE.Group();
    wheel.position.set(wheelAt.x, 0.62, wheelAt.z);
    const rimM = this.std('#34373d', { roughness: 0.5, metalness: 0.55 });
    const padM = this.std('#4a3a30', { roughness: 0.9 });
    for (const k of [-1, 1]) {
      const rim = new THREE.Mesh(this.track(new THREE.TorusGeometry(1.1, 0.06, 5, 20)), rimM);
      rim.position.x = k * 0.3;
      rim.rotation.y = Math.PI / 2;
      wheel.add(rim);
    }
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const pad = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.7, 0.08, 0.36)), padM);
      pad.position.set(0, Math.sin(a) * 1.05, Math.cos(a) * 1.05);
      pad.rotation.x = -a;
      wheel.add(pad);
      const spoke = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.06, 0.06, 1.1)), rimM);
      spoke.position.set(0, Math.sin(a) * 0.55, Math.cos(a) * 0.55);
      spoke.rotation.x = -a;
      wheel.add(spoke);
    }
    this.group.add(wheel);
    this.wheel = wheel;
    if (jammed) {
      // Chains round the wheel and the helve; wedges driven in; the clan's tools left lying.
      const chainM = this.std('#5a5f68', { roughness: 0.4, metalness: 0.7 });
      for (let i = 0; i < 4; i++) {
        const c = new THREE.Mesh(this.track(new THREE.TorusGeometry(i < 2 ? 1.12 : 0.24, 0.035, 4, 16)), chainM);
        if (i < 2) {
          c.position.set(wheelAt.x + (i ? 0.15 : -0.15), 0.62, wheelAt.z);
          c.rotation.set(0, Math.PI / 2, 0.4 * (i ? 1 : -1));
        } else {
          c.position.set(at.x + (i === 2 ? -0.3 : 0.3), 1.85, at.z - 0.6);
          c.rotation.set(0, 0, Math.PI / 2);
        }
        this.group.add(c);
        this.chains.push(c);
      }
      for (const k of [-1, 1]) kit.add(box, '#6a5a48', place(at.x + k * 0.25, 1.95, at.z - 0.4, 0, new THREE.Vector3(0.12, 0.3, 0.2), 0.3, 0));
      kit.add(box, IRON, place(at.x - 1.4, 0.05, at.z + 0.9, 0.7, new THREE.Vector3(0.7, 0.06, 0.1)));
      kit.add(box, IRON, place(at.x - 1.2, 0.08, at.z + 1.1, 0.7, new THREE.Vector3(0.16, 0.12, 0.2)));
      kit.add(box, '#4a3a2e', place(at.x + 1.5, 0.05, at.z + 1.1, -0.4, new THREE.Vector3(0.08, 0.06, 0.8)));
      kit.add(box, IRON, place(at.x + 1.5, 0.08, at.z + 1.5, -0.4, new THREE.Vector3(0.3, 0.12, 0.12)));
    }
    box.dispose();
    cyl.dispose();
  }

  /** The heart stone set in the rock: a lump of rock with a red crystal cluster in it, beating. */
  private heartStone(kit: Kit, hot: Kit, p: THREE.Vector3): void {
    this.rock(kit, p.x, p.z, 0.9, 1.3, 3, ['#4a3530', '#3e2c28', '#56403a', '#36272a']);
    const oct = new THREE.OctahedronGeometry(0.5, 0);
    for (let i = 0; i < 4; i++) hot.add(oct, i === 0 ? '#ff5a3a' : '#c42a1c', place(p.x + (i - 1.5) * 0.14, 0.85 + (i % 2) * 0.12, p.z + 0.32, i * 0.7, new THREE.Vector3(0.2, 0.42 - i * 0.05, 0.2), 0.3 * (i - 1.5), 0));
    oct.dispose();
    this.stoneGlow = new THREE.Vector3(p.x, 0.95, p.z + 0.35);
  }

  private buildRiver(kit: Kit, far: Kit, hot: Kit, s: BattleState): void {
    const L = emberLayout(s.seed);
    this.E = L;
    this.setOpen(L.floor);
    this.underfloor(far, -0.6);
    this.river(kit, far, hot, -3.5);
    this.seams(hot, L.floor, 24);
    for (const b of L.boulders) {
      const p = this.hex(b.q, b.r, 0);
      this.rock(kit, p.x, p.z, 0.9, 1.1, b.q + b.r);
    }
    for (const t of L.tunnels) this.tunnel(kit, t, 7.6 * 0.95);
    this.cavernWall(far, hot, 10.4, 9.4);
    const at = this.ctx.at(L.hammer.x, L.hammer.y, 0);
    this.hammer(kit, at.clone().setZ(at.z - 0.3), this.ctx.at(L.hammer.x + 2600, L.hammer.y - 800, 0), true);
    this.heartStone(kit, hot, this.ctx.at(L.stone.x - 200, L.stone.y - 200, 0));
    this.discPool();
  }

  private buildCams(kit: Kit, far: Kit, hot: Kit, s: BattleState): void {
    const L = camsLayout(s.seed);
    const seq = s.rules?.senda?.cams?.seq ?? L.seq;
    this.C = L;
    this.setOpen(L.floor);
    this.underfloor(far, -0.6);
    this.river(kit, far, hot, -3.5);
    this.seams(hot, L.floor, 18);
    this.cavernWall(far, hot, 9.6, 8.6);
    const stone = this.ctx.at(L.stone.x, L.stone.y, 0);
    const wheel = this.ctx.at(L.wheel.x, L.wheel.y, 0);
    // The test's trip-hammer, built big (a phone first showed it as a thin band over an empty floor, its frame and
    // head one dark block): a tall frame of pale timber with iron bands, the head of bright iron with a red-hot face,
    // the six-faced base twice a hex across turning on the shaft, the shaft's bolts and red stripes going round with it,
    // an arrow for the way it turns, the face under the hammer lit.
    const Y = DRUM_Y;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const woodA = '#a27c5a', woodB = '#8c6a4c', band = '#7d848e';
    for (const k of [-1, 1]) {
      const x = stone.x + k * (DRUM_LEN / 2 + 0.32);
      kit.add(box, k > 0 ? woodA : woodB, place(x, 2.3, stone.z - 0.1, 0, new THREE.Vector3(0.36, 4.6, 0.42)));
      kit.add(box, woodB, place(x + k * 0.55, 1.0, stone.z - 0.1, 0, new THREE.Vector3(0.18, 2.1, 0.2), 0, k * 0.5));
      for (const y of [0.4, 2.0, 3.9]) kit.add(box, band, place(x, y, stone.z - 0.1, 0, new THREE.Vector3(0.42, 0.1, 0.48)));
    }
    kit.add(box, woodA, place(stone.x, 4.62, stone.z - 0.1, 0, new THREE.Vector3(DRUM_LEN + 1.6, 0.34, 0.5)));
    kit.add(box, band, place(stone.x, 4.82, stone.z - 0.1, 0, new THREE.Vector3(DRUM_LEN + 1.7, 0.08, 0.54)));
    // The pivot's post behind (north), the helve on it.
    kit.add(box, woodB, place(stone.x, 1.9, stone.z - HELVE_BACK, 0, new THREE.Vector3(0.5, 3.8, 0.5)));
    kit.add(box, band, place(stone.x, 3.75, stone.z - HELVE_BACK, 0, new THREE.Vector3(0.6, 0.14, 0.6)));
    const helve = new THREE.Group();
    helve.position.set(stone.x, HELVE_Y, stone.z - HELVE_BACK);
    const hw = this.std('#b08a66', { roughness: 0.85 });
    const hi = this.std('#9aa2ae', { roughness: 0.35, metalness: 0.6 });
    const arm = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.3, 0.3, HELVE_BACK + 0.5)), hw);
    arm.position.set(0, 0, (HELVE_BACK + 0.5) / 2 - 0.25);
    helve.add(arm);
    const head = new THREE.Mesh(this.track(new THREE.BoxGeometry(1.25, 1.1, 1.1)), hi);
    head.position.set(0, -0.35, HELVE_BACK);
    head.castShadow = true;
    helve.add(head);
    for (const y of [-0.05, -0.62]) {
      const b = new THREE.Mesh(this.track(new THREE.BoxGeometry(1.32, 0.1, 1.16)), this.std('#b84a34', { roughness: 0.5, metalness: 0.4 }));
      b.position.set(0, y, HELVE_BACK);
      helve.add(b);
    }
    // The striking face: hot iron, brighter when it lands.
    this.headFace = this.track(new THREE.MeshBasicMaterial({ color: '#ff7a4a', fog: false }));
    const face = new THREE.Mesh(this.track(new THREE.BoxGeometry(1.1, 0.08, 0.96)), this.headFace);
    face.position.set(0, -0.93, HELVE_BACK);
    helve.add(face);
    this.group.add(helve);
    this.helve = helve;
    // The base: six faces round the shaft, their numbers cut in; the heart stone set in its west end, red.
    const drum = new THREE.Group();
    drum.position.set(stone.x, Y, stone.z);
    const prism = new THREE.Mesh(this.track(new THREE.CylinderGeometry(DRUM_R, DRUM_R, DRUM_LEN, 6, 1)), this.std('#b2958a', { roughness: 0.9 }));
    prism.rotation.z = Math.PI / 2;
    prism.castShadow = true;
    drum.add(prism);
    for (const k of [-1, 1]) {
      const rim = new THREE.Mesh(this.track(new THREE.CylinderGeometry(DRUM_R * 1.03, DRUM_R * 1.03, 0.1, 6, 1)), this.std('#6e757f', { roughness: 0.4, metalness: 0.6 }));
      rim.rotation.z = Math.PI / 2;
      rim.position.x = k * (DRUM_LEN / 2 - 0.05);
      drum.add(rim);
    }
    const heart = new THREE.Mesh(this.track(new THREE.OctahedronGeometry(0.62, 0)), this.track(new THREE.MeshBasicMaterial({ color: '#ff5a32', fog: false })));
    heart.position.set(-DRUM_LEN / 2 - 0.12, 0, 0);
    heart.scale.set(0.5, 1, 1);
    drum.add(heart);
    for (let f = 0; f < 6; f++) {
      const a = (f * Math.PI) / 3;
      // Live faces (in the veins' order) carry a red vein round their number; the rest are bare rock.
      const live = seq.includes(f);
      const mat = this.track(new THREE.MeshBasicMaterial({ map: this.track(numTex(String(f + 1), true)), transparent: true, color: '#e8e4e2', depthWrite: false, fog: false }));
      const plate = new THREE.Mesh(this.track(new THREE.PlaneGeometry(DRUM_R * 0.86, DRUM_R * 0.86)), mat);
      // Face f's centre: up (+y) when the drum is at f's angle (the drum turns about x).
      // (Its normal (0, cos a, −sin a): a turn of −a − ¼ about x from the plane's +z.)
      plate.position.set(0.04, Math.cos(a) * DRUM_R * 0.875, -Math.sin(a) * DRUM_R * 0.875);
      plate.rotation.set(-a - Math.PI / 2, 0, 0);
      plate.renderOrder = 4;
      drum.add(plate);
      if (live) {
        const vein = new THREE.Mesh(this.track(new THREE.PlaneGeometry(DRUM_LEN * 0.92, DRUM_R * 0.92)), this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0.55, depthWrite: false, fog: false })));
        vein.position.set(0, Math.cos(a) * DRUM_R * 0.872, -Math.sin(a) * DRUM_R * 0.872);
        vein.rotation.set(-a - Math.PI / 2, 0, 0);
        vein.renderOrder = 3;
        drum.add(vein);
        this.facePlates.push({ mat, f, vein: vein.material as THREE.MeshBasicMaterial });
      } else this.facePlates.push({ mat, f, vein: null });
    }
    this.chipMat = this.std('#1a1412', { roughness: 1 });
    this.group.add(drum);
    this.drum = drum;
    this.faceAt = new THREE.Vector3(stone.x, Y + DRUM_R, stone.z);
    this.stoneGlow = new THREE.Vector3(stone.x - DRUM_LEN / 2 - 0.2, Y, stone.z);
    // The anvil block under the base, and the arrow of its turn on its east end (the way the faces come up).
    kit.add(box, DRESSED[2] as string, place(stone.x, 0.12, stone.z, 0, new THREE.Vector3(DRUM_LEN + 0.4, 0.24, 2.0)));
    const arrowM = this.track(new THREE.MeshBasicMaterial({ color: '#ff8a6e', fog: false, side: THREE.DoubleSide }));
    const turnArc = new THREE.Mesh(this.track(new THREE.TorusGeometry(DRUM_R + 0.28, 0.06, 4, 24, Math.PI * 0.9)), arrowM);
    const tip = new THREE.Mesh(this.track(new THREE.ConeGeometry(0.17, 0.4, 4)), arrowM);
    const turn = new THREE.Group();
    turn.add(turnArc);
    // (The faces pass the hammer 1, 2, 3…: the drum turns +x; the arrow's head where the next face comes from.)
    tip.position.set(Math.cos(Math.PI * 0.9) * (DRUM_R + 0.28), Math.sin(Math.PI * 0.9) * (DRUM_R + 0.28), 0);
    tip.rotation.z = Math.PI * 0.9;
    turn.add(tip);
    turn.rotation.y = Math.PI / 2;
    turn.rotation.x = Math.PI * 0.05;
    turn.position.set(stone.x + DRUM_LEN / 2 + 0.16, Y, stone.z);
    this.group.add(turn);
    // The veins' order over the frame: a tablet per blow, its face's number, arrows between.
    seq.forEach((f, i) => {
      const mat = this.track(new THREE.MeshBasicMaterial({ map: this.track(numTex(String(f + 1))), color: '#ff6a50', transparent: true, depthWrite: false, fog: false }));
      const mesh = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.8, 0.8)), mat);
      // (On the beam's front: higher up, the HUD's panel hid them on a desktop.)
      mesh.position.set(stone.x - ((seq.length - 1) * 0.95) / 2 + i * 0.95, 4.75, stone.z + 0.45);
      mesh.rotation.x = -0.35;
      mesh.renderOrder = 5;
      this.group.add(mesh);
      this.tablets.push({ mesh, mat });
      if (i < seq.length - 1) hot.add(box, '#ff8a6e', place(mesh.position.x + 0.475, 4.75, stone.z + 0.5, 0, new THREE.Vector3(0.12, 0.04, 0.04)));
    });
    // The shaft from the base to the wheel, at the base's height: it turns, its bolts and stripes with it.
    const dx = wheel.x - stone.x;
    const shaftG = new THREE.Group();
    shaftG.position.set(stone.x + dx / 2, Y, wheel.z);
    const sm = this.std('#7a818c', { roughness: 0.4, metalness: 0.55 });
    const rod = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.17, 0.17, dx, 12)), sm);
    rod.rotation.z = Math.PI / 2;
    shaftG.add(rod);
    const stripeM = this.std('#c43a26', { roughness: 0.6 });
    for (let i = 0; i < 14; i++) {
      const s0 = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.22, 0.06, 0.38)), i % 2 ? stripeM : sm);
      s0.position.set(-dx / 2 + 0.8 + (i * (dx - 1.6)) / 13, 0.17, 0);
      const pivot = new THREE.Group();
      pivot.rotation.x = (i * Math.PI) / 2;
      pivot.add(s0);
      shaftG.add(pivot);
    }
    this.group.add(shaftG);
    this.shaftG = shaftG;
    for (let k = 0; k <= 6; k++) {
      const x = stone.x + DRUM_LEN / 2 + 0.6 + (k * (dx - DRUM_LEN / 2 - 1.9)) / 6;
      if (k % 2) continue;
      kit.add(box, woodB, place(x, Y / 2, wheel.z, 0, new THREE.Vector3(0.22, Y, 0.62)));
      kit.add(box, woodA, place(x, Y - 0.14, wheel.z, 0, new THREE.Vector3(0.4, 0.14, 0.72)));
    }
    // The wheel: a paddle wheel of iron and timber, its paddles in the river's heat.
    const wg = new THREE.Group();
    wg.position.set(wheel.x, Y, wheel.z);
    const wrimM = this.std('#6a717b', { roughness: 0.45, metalness: 0.6 });
    const padM = this.std('#8c6a4c', { roughness: 0.9 });
    for (const k of [-1, 1]) {
      const rim = new THREE.Mesh(this.track(new THREE.TorusGeometry(1.45, 0.08, 5, 24)), wrimM);
      rim.position.x = k * 0.36;
      rim.rotation.y = Math.PI / 2;
      wg.add(rim);
    }
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      const pad = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.86, 0.1, 0.42)), padM);
      pad.position.set(0, Math.sin(a) * 1.4, Math.cos(a) * 1.4);
      pad.rotation.x = -a;
      wg.add(pad);
      const spoke = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.07, 0.07, 1.4)), wrimM);
      spoke.position.set(0, Math.sin(a) * 0.7, Math.cos(a) * 0.7);
      spoke.rotation.x = -a;
      wg.add(spoke);
    }
    this.group.add(wg);
    this.wheel = wg;
    // A light on the machine (the river's glow alone left it a silhouette).
    this.light(new THREE.Vector3(stone.x + 0.4, Y + 1.6, stone.z + 1.2), '#ffd8c4', 3.2, 0.35);
    this.light(new THREE.Vector3(stone.x + dx / 2, Y + 0.4, wheel.z + 0.8), '#ffb48a', 4.5, 0.22);
    // The six cam places on the shaft: a hub and its plate with the face's number, facing the bank.
    const hub = new THREE.CylinderGeometry(0.3, 0.3, 0.16, 12);
    L.slots.forEach((h, k) => {
      const p = this.hex(h.q, h.r, 0);
      this.hubAt.push(new THREE.Vector3(p.x, Y, wheel.z));
      kit.add(hub, '#5c636d', place(p.x, Y, wheel.z, 0, 1, 0, Math.PI / 2));
      const plate = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.66, 0.66)), this.track(new THREE.MeshBasicMaterial({ map: this.track(numTex(String(k + 1))), transparent: true, depthWrite: false })));
      plate.position.set(p.x, 0.42, wheel.z + 0.62);
      plate.rotation.x = -0.6;
      this.group.add(plate);
      kit.add(box, band, place(p.x, Y / 2 - 0.1, wheel.z + 0.5, 0, new THREE.Vector3(0.07, Y - 0.2, 0.07)));
    });
    // The cams: red iron lobes on a collar.
    const lobeM = this.std('#d23a24', { roughness: 0.45, metalness: 0.35, emissive: new THREE.Color('#5a120a') });
    const collarM = this.std('#6a717b', { roughness: 0.4, metalness: 0.6 });
    for (let k = 0; k < 3; k++) {
      const g = new THREE.Group();
      const lobe = new THREE.Group();
      const collar = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.4, 0.4, 0.24, 12)), collarM);
      collar.rotation.z = Math.PI / 2;
      lobe.add(collar);
      const tooth = new THREE.Mesh(this.track(new THREE.ConeGeometry(0.32, 0.75, 3)), lobeM);
      tooth.position.y = 0.6;
      tooth.scale.z = 0.5;
      lobe.add(tooth);
      g.add(lobe);
      g.castShadow = true;
      this.group.add(g);
      this.cams.push({ g, lobe, pos: new THREE.Vector3() });
    }
    // The rack where the cams lie: a timber trestle each.
    for (const h of L.lie) {
      const p = this.hex(h.q, h.r, 0);
      this.rackAt.push(new THREE.Vector3(p.x, 0.5, p.z));
      for (const k of [-1, 1]) kit.add(box, '#8c6a4c', place(p.x + k * 0.42, 0.25, p.z, 0, new THREE.Vector3(0.12, 0.5, 0.6)));
      kit.add(box, '#a27c5a', place(p.x, 0.5, p.z, 0, new THREE.Vector3(1.05, 0.1, 0.4)));
    }
    // The lever: an iron bar on a pivot, a red grip.
    const lp = this.hex(L.lever.q, L.lever.r, 0);
    kit.add(box, DRESSED[0] as string, place(lp.x, 0.15, lp.z, 0, new THREE.Vector3(0.6, 0.3, 0.6)));
    const lever = new THREE.Group();
    lever.position.set(lp.x, 0.32, lp.z);
    const bar = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.08, 1.2, 0.08)), collarM);
    bar.position.y = 0.6;
    lever.add(bar);
    const grip = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.11, 8, 6)), lobeM);
    grip.position.y = 1.22;
    lever.add(grip);
    lever.rotation.z = 0.5;
    this.group.add(lever);
    this.lever = lever;
    // The arc of the crab's pincers (shown while they are open), and the bank's rim for its splash.
    const arcM = this.track(new THREE.MeshBasicMaterial({ color: RED, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }));
    const arcA = (SENDA.cams.boss.arc / 1024) * Math.PI * 2;
    const arc = new THREE.Mesh(this.track(new THREE.RingGeometry(0.4, (SENDA.cams.boss.reach / 1000) * S, 24, 1, -arcA, arcA * 2).rotateX(-Math.PI / 2)), arcM);
    arc.renderOrder = 3;
    arc.visible = false;
    this.group.add(arc);
    this.arc = arc;
    this.arcMat = arcM;
    const rimM = this.track(new THREE.MeshBasicMaterial({ color: FIRE, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
    const rim = new THREE.Mesh(this.track(new THREE.RingGeometry(7.2, 8.6, 40).rotateX(-Math.PI / 2)), rimM);
    rim.position.set(0, 0.04, 0.5);
    rim.renderOrder = 2;
    this.group.add(rim);
    this.rimMat = rimM;
    for (let i = 0; i < 3; i++) {
      const m = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.2, 0.16, 0.1)), this.chipMat);
      m.visible = false;
      this.chipMarks.push(m);
      drum.add(m);
    }
    this.discPool();
    box.dispose();
    hub.dispose();
  }

  /* ---------- the scenes ---------- */

  private buildSlab(kit: Kit, far: Kit, hot: Kit): void {
    this.underfloor(far, -0.6);
    this.river(kit, far, hot, -2.5);
    // The slab: a warm flat rock, seams of fire under its edge.
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, '#6a4c43', place(0, -0.05, 0.2, 0.1, new THREE.Vector3(4.8, 0.16, 3.2)));
    kit.add(box, '#5e433b', place(0.3, -0.18, 0.3, -0.2, new THREE.Vector3(5.4, 0.2, 3.8)));
    for (let i = 0; i < 6; i++) hot.add(box, '#ff6a2a', place(-2.2 + i * 0.9, -0.12, 1.75 + Math.sin(i) * 0.2, 0, new THREE.Vector3(0.6, 0.04, 0.05)));
    for (let a = 0; a < 16; a++) {
      const ang = Math.PI * 0.1 + (a / 15) * Math.PI * 0.8;
      this.rock(far, Math.cos(ang) * 7.2, Math.sin(ang) * 5.6 + 0.6, 2.2 + Math.sin(a * 2.1), 1.8, a);
    }
    this.light(new THREE.Vector3(0, 0.3, -1.4), '#ff8a3a', 3.4, 0.4, 0.5);
    box.dispose();
  }

  /** Grada's wall at dawn, the Raja opening over it (the opening's cinematic: its video to come). */
  private buildRaja(kit: Kit, far: Kit): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    // A ledge of the quarry on the board; the wall rising north; houses on its ledges; the Raja over them.
    const z = -5.5;
    for (let x = -16; x <= 16; x += 1.6) {
      const j = Math.sin(x * 12.9) * 0.5 + 0.5;
      far.add(box, ['#3c434d', '#343a43', '#47505b'][Math.floor(j * 2.99)] as string, place(x, 6, z - j * 1.2, 0, new THREE.Vector3(1.8, 16, 2.2)));
    }
    for (let li = 0; li < 3; li++) {
      const ly = 1.4 + li * 2.6;
      const lz = z + 1.6;
      kit.add(box, '#4f565f', place(0, ly - 0.35, lz, 0, new THREE.Vector3(14, 0.7, 2.2)));
      for (let i = 0; i < 6; i++) {
        const hx = -5.5 + i * 2.1 + (li % 2) * 0.8;
        kit.add(box, ['#7c7f84', '#6c7076', '#868a8f'][(i + li) % 3] as string, place(hx, ly + 0.55, lz - 0.2, 0, new THREE.Vector3(1.4, 1.1, 1.1)));
        for (const sgn of [-1, 1]) kit.add(box, '#2b2f36', place(hx, ly + 1.3, lz - 0.2 + sgn * 0.28, 0, new THREE.Vector3(1.55, 0.1, 0.7), sgn * 0.62, 0));
        kit.add(box, '#e8b48e', place(hx + 0.3, ly + 0.65, lz + 0.36, 0, new THREE.Vector3(0.28, 0.24, 0.03)));
        this.light(new THREE.Vector3(hx + 0.3, ly + 0.65, lz + 0.5), WARM, 0.9, 0.5, 0.6);
      }
    }
    // The Raja: a black crack across the wall over the houses, red deep in it, wider in the middle (it opened).
    for (let x = -15; x < 15; x += 0.8) {
      const yy = 10.5 + Math.sin(x * 1.9) * 0.5 + Math.sin(x * 0.37) * 1.1;
      const wid = 0.4 + (Math.abs(x) < 5 ? 0.6 : 0.2) + (Math.sin(x * 0.7) * 0.5 + 0.5) * 0.4;
      kit.add(box, '#06070a', place(x, yy, z + 1.1, 0, new THREE.Vector3(1.0, wid, 0.4), 0, Math.cos(x * 1.9) * 0.5));
      if (Math.floor(x / 0.8) % 2 === 0) this.light(new THREE.Vector3(x, yy, z + 1.4), '#c2361e', 1.0 + wid, 0.45, 0.3);
    }
    // The stair the people climb, up the board's ledge.
    for (let k = 0; k < 8; k++) kit.add(box, '#626870', place(1.2 - k * 0.3, 0.1 + k * 0.14, 3.0 - k * 0.55, 0, new THREE.Vector3(1.3, 0.14, 0.5)));
    box.dispose();
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild): void {
    const c = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const k = 0.5 + 0.5 * Math.sin(v.x * 1.7 + v.z * 0.9) * Math.cos(v.z * 1.3 - v.x * 0.5);
        if (this.place === 'deepRoad') c.set(FLAG[Math.floor(k * 2.99)] as string).multiplyScalar(0.85 + lum * 0.3);
        else if (this.place === 'rajaWall') c.set(['#5a6068', '#636a72', '#535961'][Math.floor(k * 2.99)] as string).multiplyScalar(0.85 + lum * 0.3);
        else c.set(ROCK[Math.floor(k * 3.99)] as string).multiplyScalar(0.88 + lum * 0.3);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    this.carve(terrain);
    if (this.place === 'emberSlab') terrain.mesh.visible = false;
    if (terrain.fog) terrain.fog.group.visible = false;
    if (this.outside) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
    }
    this.paint();
  }

  paint(): void {
    const look = this.outside ? LOOK.dawn : LOOK.inside;
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    if (this.dome) {
      this.dome.set('#2a3550', '#8790a6', '#ffc8b2');
      this.dome.u.uSunDir.value.set(0.7, 0.3, -0.4);
    }
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const at = (h = 0.2): THREE.Vector3 => this.ctx.at(e.x, e.y, h);
      if (e.kind === 'ventBlow') {
        const v = this.ventGlow[e.n ?? 0];
        if (v) {
          this.ctx.shake(0.18);
          this.ctx.vfx.burst(v.at.clone().setY(0.4), { n: 26, color: '#ffb45a', speed: 2.6, up: 3.2, size: 0.16, life: 14, gravity: -0.5, spread: v.w * 0.45 });
          this.ctx.flare(v.at.clone().setY(1.0), '#ff9a4a', 3.5, 12);
        }
      } else if (e.kind === 'ventWarn') {
        const v = this.ventGlow[e.n ?? 0];
        if (v) this.ctx.vfx.burst(v.at.clone().setY(0.2), { n: 10, color: '#ff6a2a', speed: 0.6, up: 1.4, size: 0.08, life: 18, spread: v.w * 0.4 });
      } else if (e.kind === 'burn') {
        this.ctx.vfx.burst(at(0.8), { n: 14, color: '#ffb45a', speed: 1.6, up: 1.4, size: 0.12, life: 10, smoke: true });
      } else if (e.kind === 'holeOut') {
        this.ctx.vfx.burst(at(0.6), { n: 18, color: '#5a5450', speed: 1.4, up: 1.0, size: 0.4, life: 22, smoke: true });
      } else if (e.kind === 'chalk') {
        this.ctx.vfx.ring(at(0.06), 0.2, 1.2, '#ecf0f4', 10, 0.6);
      } else if (e.kind === 'mouth') {
        this.ctx.shake(0.3);
        if (this.mouthGlow) this.ctx.flare(this.mouthGlow, '#ffb45a', 6, 24);
      } else if (e.kind === 'pulseWarn') {
        this.ctx.vfx.burst(at(0.1), { n: 6, color: '#ff6a2a', speed: 0.5, up: 0.8, size: 0.08, life: 14 });
      } else if (e.kind === 'pulse') {
        this.ctx.shake(0.22);
        this.ctx.vfx.burst(at(0.2), { n: 28, color: '#ffa040', speed: 2.4, up: 3.4, size: 0.16, life: 16, gravity: 0.6 });
        this.ctx.vfx.burst(at(0.4), { n: 8, color: '#3a2a26', speed: 1.2, up: 1.6, size: 0.5, life: 26, smoke: true });
        this.ctx.flare(at(0.6), '#ff8a3a', 3, 10);
      } else if (e.kind === 'pry') {
        // Dunna at the hammer: a chain or a wedge off, now and then.
        this.ctx.vfx.burst(at(1.0), { n: 6, color: '#ffd0a0', speed: 1.4, up: 0.8, size: 0.06, life: 8, gravity: 1 });
      } else if (e.kind === 'free') {
        this.ctx.shake(0.5);
        if (this.wheel) this.ctx.flare(this.wheel.position.clone().setY(1.4), '#ffffff', 4, 18);
        for (const c of this.chains) this.ctx.vfx.burst(c.position.clone(), { n: 12, color: '#9aa0a8', speed: 2, up: 1.5, size: 0.08, life: 12, gravity: 1 });
      } else if (e.kind === 'camPut' || e.kind === 'camTake') {
        this.ctx.vfx.burst(at(0.6), { n: 8, color: '#ffb48a', speed: 1.2, up: 0.8, size: 0.08, life: 8, gravity: 1 });
      } else if (e.kind === 'camTurn') {
        this.leverPull = 1;
        this.ctx.shake(0.15);
      } else if (e.kind === 'leverHeld') {
        this.leverPull = 0.4;
        if (this.wheel) this.ctx.vfx.burst(this.wheel.position.clone(), { n: 14, color: '#ffd0a0', speed: 2, up: 1.4, size: 0.07, life: 8, gravity: 1 });
      } else if (e.kind === 'strike') {
        this.strikeFlash = 1;
        this.struck = e.n ?? -1;
        this.ctx.shake(0.45);
        if (this.faceAt) {
          this.ctx.flare(this.faceAt, '#ffffff', 3.2, 12);
          this.ctx.vfx.burst(this.faceAt.clone(), { n: 30, color: '#ffd2a0', speed: 3, up: 2.2, size: 0.09, life: 12, gravity: 1 });
          this.ctx.vfx.ring(this.faceAt.clone().setY(0.1), 0.3, 2.0, '#ffffff', 10, 0.9);
        }
      } else if (e.kind === 'chip') {
        this.ctx.shake(0.6);
        if (this.faceAt) {
          this.ctx.vfx.burst(this.faceAt.clone(), { n: 22, color: '#5a463e', speed: 2.6, up: 1.8, size: 0.16, life: 16, gravity: 1.2 });
          this.ctx.vfx.ring(this.faceAt.clone().setY(0.1), 0.3, 1.6, '#ff4f3a', 10, 0.9);
        }
        const w = s.senda?.cams;
        const m = w ? this.chipMarks[w.chips - 1] : undefined;
        if (m) {
          // The chip stays on the face struck out of turn.
          const f = e.n ?? 0;
          const a = (f * Math.PI) / 3;
          m.position.set(0.3 - (w ? w.chips : 0) * 0.22, Math.cos(a) * DRUM_R * 0.9, -Math.sin(a) * DRUM_R * 0.9);
          m.rotation.x = -a;
          m.visible = true;
        }
      } else if (e.kind === 'freed') {
        this.ctx.shake(0.9);
        if (this.stoneGlow) {
          this.ctx.flare(this.stoneGlow, '#ffffff', 6, 26);
          this.ctx.vfx.burst(this.stoneGlow.clone(), { n: 50, color: '#ff6a3a', speed: 3.4, up: 3, size: 0.12, life: 20, gravity: 0.6 });
          this.ctx.vfx.ring(this.stoneGlow.clone().setY(0.1), 0.4, 4, '#ffffff', 16, 0.9);
        }
      } else if (e.kind === 'tzOut') {
        this.ctx.shake(0.7);
        this.ctx.vfx.burst(at(0.2), { n: 40, color: '#ffa040', speed: 3, up: 3.5, size: 0.16, life: 20, gravity: 0.8 });
        this.ctx.flare(at(0.8), '#ff8a3a', 5, 18);
      } else if (e.kind === 'tzSnap') {
        this.ctx.shake(0.35);
        const p = this.ctx.pos(s.senda?.cams?.tz.id ?? -1);
        if (p) this.ctx.vfx.burst(p.clone().setY(0.6), { n: 14, color: '#ffd0a0', speed: 2.4, up: 1, size: 0.08, life: 8, gravity: 1 });
      } else if (e.kind === 'tzGrip') {
        this.ctx.shake(0.4);
      } else if (e.kind === 'tzSplash') {
        this.splashFlash = 1;
        this.ctx.shake(0.75);
        for (let k = 0; k < 9; k++) {
          const a = (k / 9) * Math.PI * 2;
          this.ctx.vfx.burst(new THREE.Vector3(Math.cos(a) * 4.5, 0.3, Math.sin(a) * 3.5 + 0.5), { n: 10, color: '#ffa040', speed: 2.2, up: 2.6, size: 0.14, life: 14, gravity: 1 });
        }
      } else if (e.kind === 'tzDown') {
        this.ctx.shake(0.8);
        this.ctx.vfx.burst(at(0.4), { n: 46, color: '#ff8a3a', speed: 3, up: 3, size: 0.18, life: 22, gravity: 0.8 });
        this.ctx.vfx.burst(at(0.6), { n: 14, color: '#2a2220', speed: 1.4, up: 2, size: 0.6, life: 30, smoke: true });
      } else if (e.kind === 'bolt') {
        const by = s.senda?.npcs.find((n) => n.id === e.npc);
        if (by) this.ctx.vfx.beam(this.ctx.at(by.x, by.y, 1.0), at(0.9), '#e8eef8', 2, 0.03);
      }
    }
  }

  draw(s: BattleState): void {
    this.last = s;
    // The jammed hammer: its chains come off as Dunna works.
    const ew = s.senda?.ember;
    const er = s.rules?.senda?.ember;
    if (ew && er && this.chains.length) {
      const k = ew.end >= 0 ? 1 : ew.prog / er.work;
      this.chains.forEach((c, i) => (c.visible = k < (i + 1) / (this.chains.length + 0.5)));
    }
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    for (const m of this.lavas) (m.uniforms.time as THREE.IUniform<number>).value = now;
    const s = this.last;
    this.glow.begin();
    // The deep road's vents: smouldering, warned, blowing.
    const vr = s?.rules?.senda?.vent;
    if (this.D && this.flames) {
      const vents = vr?.vents ?? this.D.vents.map((v) => ({ ...v }));
      const blow = vr?.blow ?? SENDA.vents.blow;
      const warn = vr?.warn ?? SENDA.vents.warn;
      const tick = s ? s.tick + Math.min(0.99, (now % (1 / 12)) * 12) : now * 12;
      let n = 0;
      const m4 = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      vents.forEach((v, i) => {
        const g = this.ventGlow[i];
        if (!g) return;
        const ph = (Math.floor(tick) + v.phase) % v.beat;
        const blowing = ph < blow;
        const warned = ph >= v.beat - warn;
        const k = warned ? (ph - (v.beat - warn)) / warn : 0;
        g.mat.opacity = blowing ? 0.75 + 0.2 * Math.sin(now * 30) : warned ? 0.18 + 0.5 * k + 0.1 * Math.sin(now * 14) : 0.07 + 0.03 * Math.sin(now * 2 + i);
        g.grate.emissive.setRGB(blowing ? 0.9 : warned ? 0.2 + 0.6 * k : 0.04, blowing ? 0.3 : warned ? 0.04 + 0.12 * k : 0.01, 0.01);
        this.glow.add(g.at.x, 0.4, g.at.z, FIRE, blowing ? 7 : warned ? 2.5 + 3 * k : 1.4, blowing ? 0.8 : warned ? 0.3 + 0.4 * k : 0.15);
        if (!blowing) return;
        const life = 1 - ph / blow;
        for (const f of this.flameAt) {
          if (f.v !== i || n >= (this.flames?.instanceMatrix.count ?? 0)) continue;
          const hgt = (1.6 + 0.9 * Math.sin(now * 17 + f.k)) * f.s * (0.6 + 0.4 * life);
          m4.compose(f.p, q, this.v3.set(f.s * (0.9 + 0.2 * Math.sin(now * 11 + f.k)), hgt, f.s));
          this.flames?.setMatrixAt(n++, m4);
        }
      });
      this.flames.count = n;
      this.flames.instanceMatrix.needsUpdate = true;
      // Grillo's lamp blinks before the blast of the vent ahead of him.
      const gr = s?.senda?.npcs.find((x) => x.id === 'grillo');
      if (gr && vr) {
        const ahead = vents.findIndex((v) => v.y1 >= gr.y);
        const v = vents[ahead];
        if (v) {
          const ph = (Math.floor(tick) + v.phase) % v.beat;
          if (ph >= v.beat - warn) {
            const p = this.ctx.at(gr.x, gr.y, 1.1);
            if (Math.sin(now * 18) > 0) this.glow.add(p.x, p.y, p.z, RED, 1.4, 0.9);
          }
        }
      }
      if (this.mouthGlow) this.glow.add(this.mouthGlow.x, this.mouthGlow.y, this.mouthGlow.z, FIRE, 6 + Math.sin(now * 1.3), 0.5);
    }
    // The hammer: jammed it shudders; free (or in a turn) it rises and falls on the cams.
    const ew = s?.senda?.ember;
    const cw = s?.senda?.cams;
    const cr = s?.rules?.senda?.cams;
    if (this.helve && this.wheel) {
      if (ew && ew.end >= 0) {
        // Free: the wheel turns in the river, the hammer goes up and down on its own.
        this.wheel.rotation.x -= dt * 1.4;
        const k = (now * 0.9) % 1;
        this.helve.rotation.x = k < 0.8 ? -0.35 * (k / 0.8) : -0.35 * (1 - (k - 0.8) / 0.2);
      } else if (ew) {
        this.helve.rotation.x = -0.05 + 0.02 * Math.sin(now * 25) * (ew.prog % 24 < 4 ? 1 : 0);
      }
    }
    if (this.drum && cw && cr) {
      // The drum: its face under the hammer; turning, a sixth of a turn a face.
      const half = cr.face / 2;
      const want = cw.turn >= 0 ? (((cw.turn - half) / cr.face) * Math.PI) / 3 : -Math.PI / 6;
      this.drumAngle += (want - this.drumAngle) * Math.min(1, dt * 12);
      if (cw.turn < 0 && Math.abs(this.drumAngle - want) > 3) this.drumAngle = want;
      this.drum.rotation.x = this.drumAngle;
      const gripped = cw.tz.mode === 2;
      if (this.wheel && !gripped) this.wheel.rotation.x = this.drumAngle;
      if (this.shaftG) this.shaftG.rotation.x = this.drumAngle;
      // The faces: the one under the hammer lit, live ones (in the veins) red, done ones white, struck one flashing.
      const seqNow = cr.seq;
      for (const fp of this.facePlates) {
        const up = Math.max(0, Math.cos((fp.f * Math.PI) / 3 - this.drumAngle));
        const k = up ** 6;
        const at = seqNow.indexOf(fp.f);
        const done = at >= 0 && at < cw.done;
        const next = at === cw.done && cw.freed < 0;
        if (done) fp.mat.color.setRGB(1, 1, 1);
        else if (at >= 0) fp.mat.color.setRGB(1, 0.5 + 0.5 * k, 0.4 + 0.6 * k);
        else fp.mat.color.setRGB(0.55 + 0.45 * k, 0.5 + 0.5 * k, 0.48 + 0.52 * k);
        if (fp.f === this.struck && this.strikeFlash > 0) fp.mat.color.setRGB(1, 1, 1);
        if (fp.vein) fp.vein.opacity = done ? 0.12 : next ? 0.5 + 0.3 * Math.sin(now * 6) : 0.38;
      }
      if (this.headFace) {
        const hot = this.strikeFlash;
        this.headFace.color.setRGB(1, 0.48 + 0.52 * hot, 0.29 + 0.71 * hot);
      }
      // The hammer rises as a cam's lobe comes up under its tail and falls at its face.
      let lift = 0;
      if (cw.turn >= 0) {
        const face = Math.floor(cw.turn / cr.face);
        const into = (cw.turn % cr.face) / cr.face;
        const set = cw.on.filter((x) => x >= 0 && x < 6);
        if (set.includes(face)) lift = into < 0.5 ? into * 2 : Math.max(0, 1 - (into - 0.5) * 8);
      }
      if (this.helve) this.helve.rotation.x += (-0.42 * lift - this.helve.rotation.x) * Math.min(1, dt * 20);
      // The cams where they are: on their place (turning with the shaft), on the rack, in the hero's hands.
      const hero = s?.heroes[0];
      this.cams.forEach((c, k) => {
        const at = cw.on[k] ?? 6 + k;
        let p: THREE.Vector3;
        if (at < 0 && hero) {
          const hp = this.ctx.pos(hero.id) ?? this.ctx.at(hero.x, hero.y, 0);
          p = this.v3.set(hp.x, hp.y + 2.2, hp.z);
          c.lobe.rotation.x = now * 2;
        } else if (at >= 0 && at < 6) {
          p = this.hubAt[at] as THREE.Vector3;
          c.lobe.rotation.x = this.drumAngle - (at * Math.PI) / 3;
        } else {
          p = this.v3.copy(this.rackAt[at - 6] ?? this.rackAt[0] ?? new THREE.Vector3()).setY(0.78);
          c.lobe.rotation.x = 0;
        }
        c.pos.lerp(p, at < 0 ? 0.5 : 0.3);
        c.g.position.copy(c.pos);
        c.g.visible = !(cw.freed >= 0 && at < 0);
      });
      // The veins' order: done white, next red and beating, to come dim.
      this.tablets.forEach((t, i) => {
        if (i < cw.done) t.mat.color.copy(WHITE);
        else if (i === cw.done && cw.freed < 0) t.mat.color.setRGB(1, 0.32 + 0.2 * Math.sin(now * 6), 0.24);
        else t.mat.color.setRGB(0.45, 0.2, 0.18);
        t.mesh.scale.setScalar(i === cw.done && cw.freed < 0 ? 1.12 + 0.06 * Math.sin(now * 6) : 1);
      });
      if (this.stoneGlow) this.glow.add(this.stoneGlow.x, this.stoneGlow.y, this.stoneGlow.z, RED, 1.6 + (cw.freed >= 0 ? 2 : 0.4 * Math.sin(now * 3)), 0.7);
      // The pincers' arc while they are open, the bank's rim before its splash.
      const tz = cw.tz;
      const crab = tz.id ? this.ctx.pos(tz.id) : undefined;
      if (this.arc && this.arcMat) {
        if (crab && tz.wind >= 0 && cr) {
          const left = Math.max(0, (tz.wind - (s?.tick ?? 0)) / Math.max(1, cr.boss.warn));
          this.arc.visible = true;
          this.arc.position.set(crab.x, 0.06, crab.z);
          // (Sim angles turn from +x toward +y, the world's +z: the ring's −y rotation.)
          this.arc.rotation.y = -(tz.aim / 1024) * Math.PI * 2;
          this.arcMat.opacity = 0.25 + 0.4 * (1 - left) + 0.12 * Math.sin(now * 14);
        } else this.arc.visible = false;
      }
      if (this.rimMat) {
        const warned = tz.splashAt >= 0;
        this.splashFlash = Math.max(0, this.splashFlash - dt * 1.5);
        this.rimMat.opacity = warned ? 0.25 + 0.25 * Math.sin(now * 12) : this.splashFlash * 0.6;
      }
      if (tz.mode === 2 && this.wheel && Math.sin(now * 20) > 0.6) this.ctx.vfx.burst(this.wheel.position.clone().setY(1.2), { n: 2, color: '#ffd0a0', speed: 2, up: 1, size: 0.06, life: 6, gravity: 1 });
      if (this.faceAt && this.strikeFlash > 0) this.glow.add(this.faceAt.x, this.faceAt.y, this.faceAt.z, WHITE, 2 + 3 * this.strikeFlash, this.strikeFlash);
      this.strikeFlash = Math.max(0, this.strikeFlash - dt * 2);
      const lava = this.lavas[0];
      if (lava) (lava.uniforms.heat as THREE.IUniform<number>).value = tz.splashAt >= 0 ? 0.8 : tz.mode === 2 ? 0.4 : 0;
    } else if (this.stoneGlow) this.glow.add(this.stoneGlow.x, this.stoneGlow.y, this.stoneGlow.z, RED, 1.4 + 0.5 * Math.sin(now * 3), 0.75);
    if (this.leverPull > 0 && this.lever) {
      this.leverPull = Math.max(0, this.leverPull - dt * 0.8);
      this.lever.rotation.z = 0.5 - Math.sin(this.leverPull * Math.PI) * 1.0;
    }
    // The magma's spots coming up: a red disc filling as it nears.
    const spots = cw?.spots ?? ew?.spots ?? [];
    const warnT = cr?.pulse.warn ?? s?.rules?.senda?.ember?.pulse.warn ?? 36;
    const rr = ((cr?.pulse.r ?? s?.rules?.senda?.ember?.pulse.r ?? 1000) / 1000) * S;
    this.discs.forEach((d, i) => {
      const p = spots[i];
      if (!p || !s) {
        d.mesh.visible = false;
        return;
      }
      const left = Math.max(0, (p.t - s.tick) / Math.max(1, warnT));
      d.mesh.visible = true;
      const at = this.ctx.at(p.x, p.y, 0.05);
      d.mesh.position.copy(at);
      d.mesh.scale.setScalar(rr * (0.35 + 0.65 * (1 - left)));
      d.mat.opacity = 0.35 + 0.35 * (1 - left) + 0.15 * Math.sin(now * 16);
      this.glow.add(at.x, 0.2, at.z, FIRE, rr * 2.2 * (1 - left * 0.6), 0.35 + 0.3 * (1 - left));
    });
    // Embers drifting up off the magma, a few at a time.
    if (this.embers.length && now > this.puff) {
      this.puff = now + 0.25;
      const v = this.embers[Math.floor((now * 7.3) % this.embers.length)] as THREE.Vector3;
      this.ctx.vfx.burst(v.clone(), { n: 2, color: '#ff8a3a', speed: 0.3, up: 1.2, size: 0.05, life: 26, gravity: -0.2 });
    }
    for (const l of this.lights) {
      const f = l.flick ? 0.86 + 0.14 * Math.sin(now * (5 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    this.glow.end();
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    for (const t of this.texs) t.dispose();
    this.dome?.dispose();
    this.flames?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}
